import React, {
  useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import Dialog from 'Components/UI/Dialog';
import useMobileLayout from 'Hooks/useMobileLayout';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { QueryVal, removeQuery } from 'Helpers/Query';
import {
} from '@material-ui/core';
import fr from 'facet-rest';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import { AttachmentPreview }
  from 'Components/ComponentLibrary/Components/Uncategorized/FileViewer/components/AttachmentPreview';
import classnames from 'classnames';
import { makeStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import DialogTitle from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/DialogTitle';
import { downloadSecureFileFromLink } from 'Helpers/File';


const useStyles = makeStyles(() => ({
  dialogRoot: {
    borderRadius: 8,
  },
  mobileDialogRoot: {
    borderRadius: 8,
  },
  dialogActions: {
    zIndex: 2,
  },
  closeButton: {
    color: 'white',
  },
}));

const FileViewer = ({
  isOpen,
  closeModal,
  attachmentResources,
  eventCategory,
}) => {
  const isMobile = useMobileLayout();
  const { rootNodeProps, logEvent } = useEventTrackingLogEvent({
    nodeName: `${eventCategory}: File Viewer`,
  });

  const classes = useStyles();

  const attachmentSelfLink = QueryVal('attachmentSelfLink');

  const [currentAttachmentSelfLink, setCurrentAttachmentSelfLink] = useState(attachmentSelfLink);
  const [currentAttachmentResource, setCurrentAttachmentResource] = useState();
  const [sendingToSecureFiles, setSendingToSecureFiles] = useState(false);

  const getAttachmentLink = (attachmentResource) => {
    const link = attachmentResource.withRelationship(
      fr.relationships.view.name,
      (viewRel) => viewRel.link('related').url(),
      () => attachmentResource.link('self').url(),
    );
    return link;
  };

  const currentIndex = useMemo(() => {
    return attachmentResources.findIndex(res => getAttachmentLink(res) === currentAttachmentSelfLink);
  }, [currentAttachmentSelfLink]);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < attachmentResources.length - 1;

  const handleClose = () => {
    removeQuery('attachmentSelfLink');
    closeModal();
  };

  useEffect(() => {
    setCurrentAttachmentSelfLink(attachmentSelfLink);
  }, [attachmentSelfLink]);

  useEffect(() => {
    if (!currentAttachmentSelfLink) {
      setCurrentAttachmentResource(attachmentResources[0]);
      return;
    }

    const res = attachmentResources.find(resource => {
      const attachmentViewLink = getAttachmentLink(resource);
      return attachmentViewLink === currentAttachmentSelfLink;
    });

    setCurrentAttachmentResource(res);
  }, [currentAttachmentSelfLink]);

  const canSendSomeToSecureFiles = useMemo(() => {
    return attachmentResources.some(resource => resource.relationship(fr.relationships.sendToSecureFiles.name));
  }, [attachmentResources]);

  if (!currentAttachmentResource) {
    return null;
  }

  const openInNewTab = (attachmentResource) => {
    const eventName = `${eventCategory}: Clicked Open in New Tab`;
    logEvent(eventName);

    const link = document.createElement('a');
    link.download = attachmentResource.attr('name');
    link.target = '_blank';
    link.href = attachmentResource.relationship('download').link('related').url();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const presentsAsDownloadable = (attachmentResource) => attachmentResource.withRelationship(fr.relationships.presentsAsDownloadable.name, () => true, () => false);
  const downloadAttachment = async (attachmentResource) => {
    if (!presentsAsDownloadable(attachmentResource)) {
      return Promise.resolve();
    }

    return downloadSecureFileFromLink(attachmentResource.relationship('download').link('related'), attachmentResource.attr('name'), () => openInNewTab(attachmentResource));
  };

  const downloadAll = attachmentResources.every(presentsAsDownloadable)
    ? async () => {
      const eventName = 'Clicked Download All';
      logEvent(eventName);

      attachmentResources.forEach((attachmentResource) => {
        return downloadAttachment(attachmentResource);
      });
    }
    : undefined;

  const sendToSecureFiles = async (attachmentResource, setLoading = true) => {
    if (!attachmentResource.relationship(fr.relationships.sendToSecureFiles.name)) {
      return Promise.resolve();
    }

    if (setLoading) {
      setSendingToSecureFiles(true);
    }

    return attachmentResource.relationship(fr.relationships.sendToSecureFiles.name).link('related').get()
      .then(([resource]) => {
        return resource.submit({
          exportArgs: {
            exportAttributesAsIs: true,
          },
        });
      })
      .then(() => {
        displaySuccess(`Moved ${attachmentResource.attr('name')} to secure files`);
      })
      .catch((err) => {
        displayErrorMessage(err, err.errors);
      })
      .finally(() => {
        if (setLoading) {
          setSendingToSecureFiles(false);
        }
      });
  };

  const sendAllToSecureFiles = async () => {
    const eventName = 'Clicked Download All';
    logEvent(eventName);

    setSendingToSecureFiles(true);
    return Promise.all(attachmentResources.map((attachmentResource) => {
      return sendToSecureFiles(attachmentResource, false);
    })).finally(() => {
      setSendingToSecureFiles(false);
    });
  };

  const onPrev = () => {
    if (!hasPrev) {
      return;
    }

    setCurrentAttachmentSelfLink(getAttachmentLink(attachmentResources[currentIndex - 1]));
  };

  const onNext = () => {
    if (!hasNext) {
      return;
    }
    setCurrentAttachmentSelfLink(getAttachmentLink(attachmentResources[currentIndex + 1]));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        handleClose();
      }}
      classes={{
        actions: classes.dialogActions,
        closeButton: classes.closeButton,
      }}
      fullScreen={isMobile}
      PaperProps={{
        className: classnames(
          {
            [classes.dialogRoot]: !isMobile,
            [classes.mobileDialogRoot]: isMobile,
          },
        ),
        test: 'file-viewer',
      }}
      maxWidth={'xl'}
      fullWidth
      {...rootNodeProps}
    >
      <DialogTitle color="primary">{currentAttachmentResource.attr('name')}</DialogTitle>
      <AttachmentPreview
        attachmentResource={currentAttachmentResource}
        onPrev={hasPrev ? () => {
          onPrev();
        } : null}
        onNext={hasNext ? () => {
          onNext();
        } : null}
        extraActions={[
          {
            prompt: 'Download All',
            onClick: downloadAll,
            hidden: !downloadAll,
            testName: 'download-all',
          },
          {
            prompt: 'Send All to Secure Files',
            onClick: sendAllToSecureFiles,
            disabled: sendingToSecureFiles,
            hidden: !canSendSomeToSecureFiles,
            testName: 'send-all-to-secure-files',
          },
          {
            prompt: 'Open in New Tab',
            onClick: () => openInNewTab(currentAttachmentResource),
            hidden: !presentsAsDownloadable(currentAttachmentResource),
            testName: 'open-in-new-tab',
          },
        ].filter(action => !action.hidden)}
      />
    </Dialog>
  );
};

FileViewer.defaultProps = {
};

FileViewer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  attachmentResources: PropTypes.array.isRequired,
  eventCategory: PropTypes.string.isRequired,
};

export { FileViewer };
